import React, { useRef } from 'react';
import { ChromePicker } from '@hello-pangea/color-picker';
import { Input, Modal } from 'antd';
import { publicRuntimeConfig } from 'config/index';
import { Formik } from 'formik';

import { addErrorMessage, FormField, FormikFieldTouched } from 'components/common';
import { classnames } from 'lib/classnames';
import { CHARACTER_SIZE, SMALL_MODAL_WIDTH } from 'lib/global';
import useI18n from 'lib/hooks/useI18n';
import { FormError } from 'lib/types/error';
import { Priority as PriorityProps } from 'lib/types/models';

import styles from './index.module.less';

const cx = classnames(styles);
const { TextArea } = Input;

interface PriorityModalType {
  modalVisible: boolean;
  initialValue: PriorityProps;
  onSubmit: (value: PriorityProps) => void;
  handleCloseModal: () => void;
  title: string;
  loading: boolean;
  allPriorityList?: PriorityProps[];
}

const DEFAULT_COLOR = '#DA1414';

const PriorityModal: React.VFC<PriorityModalType> = ({
  modalVisible,
  initialValue,
  onSubmit,
  handleCloseModal,
  title,
  loading,
  allPriorityList,
}) => {
  const { t } = useI18n();
  const { isDemo } = publicRuntimeConfig;
  const form = useRef(null);
  const initialValues = {
    name: initialValue?.name,
    key: initialValue?.key,
    color: initialValue?.color || DEFAULT_COLOR,
    description: initialValue?.description,
    order: initialValue?.order,
  };

  const validate = async value => {
    const errors = {} as FormError;
    if (!value.name) {
      errors.name = addErrorMessage(errors.name, t('message.users.tips.name'));
    } else if (
      value.name.length < CHARACTER_SIZE.MIN_SYSTEM_NAME_SPECIAL_LENGTH ||
      value.name.length > CHARACTER_SIZE.MAX_SYSTEM_NAME_SPECIAL_LENGTH
    ) {
      errors.name = addErrorMessage(
        errors.name,
        t('message.fields.priorityName', {
          MIN_LENGTH: CHARACTER_SIZE.MIN_SYSTEM_NAME_SPECIAL_LENGTH,
          MAX_LENGTH: CHARACTER_SIZE.MAX_SYSTEM_NAME_SPECIAL_LENGTH,
        }),
      );
    } else if (
      allPriorityList.some(v => {
        return v.name === value.name;
      }) &&
      initialValues?.name !== value.name
    ) {
      errors.name = addErrorMessage(errors.name, t('message.chat.alreadyExist'));
    }
    if (!value.color) {
      errors.color = addErrorMessage(errors.color, t('validate.fields.selectColor'));
    }
    return errors;
  };

  return (
    <Formik innerRef={form} initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
      {({ handleSubmit }) => (
        <Modal
          maskClosable={false}
          confirmLoading={loading}
          width={SMALL_MODAL_WIDTH}
          title={title}
          open={modalVisible}
          onOk={() => handleSubmit()}
          onCancel={handleCloseModal}
          okButtonProps={{ disabled: isDemo }}
        >
          <FormikFieldTouched />
          <FormField name="name" label={t('global.name')} required>
            {({ field }) => (
              <Input
                maxLength={CHARACTER_SIZE.MAX_SYSTEM_NAME_SPECIAL_LENGTH}
                placeholder={t('placeholder.fields.inputPriorityName', {
                  MIN_LENGTH: CHARACTER_SIZE.MIN_SYSTEM_NAME_SPECIAL_LENGTH,
                  MAX_LENGTH: CHARACTER_SIZE.MAX_SYSTEM_NAME_SPECIAL_LENGTH,
                })}
                {...field}
                onChange={e => field.onChange(e.target.value?.trim())}
              />
            )}
          </FormField>
          <FormField name="color" label={t('formField.fields.colorPriority')} required>
            {({ field }) => {
              return (
                <ChromePicker
                  className={cx('color-picker')}
                  disableAlpha
                  {...field}
                  color={field.value}
                  onChange={colorCode => {
                    field.onChange(colorCode.hex);
                  }}
                />
              );
            }}
          </FormField>
          <FormField name="description" label={t('global.description')}>
            {({ field }) => (
              <TextArea
                {...field}
                maxLength={CHARACTER_SIZE.MAX_SYSTEM_DESCRIPTION_LENGTH}
                placeholder={t('placeholder.default.inputDesc')}
              />
            )}
          </FormField>
        </Modal>
      )}
    </Formik>
  );
};

export default PriorityModal;
